import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';

import CellContent from './CellContent';
import TableEditActions from './TableEditActions';
import { TableViewRow } from './types';

interface ComponentProps {
  row: TableViewRow;
  columns: ColumnDef<any>[];
  onChange: (updatedRow: TableViewRow, column: ColumnDef<any>, value: any) => void;
  onSave?: () => void;
  onCancel?: () => void;
  canSelect?: boolean;
  colCount: number;
  validateRow: (row: TableViewRow) => boolean;
  validationResults?: Record<string, { error: boolean; message?: string }>;
}

export function createPseudoCell<T>(column: ColumnDef<T>, row: any, value: any): any {
  const columnWithMeta = {
    ...column,
    meta: column.meta ?? { style: {} },
  };

  return {
    id: column.id,
    column: {
      ...column,
      columnDef: columnWithMeta,
    },
    getValue: () => value,
    getContext: () => ({
      row: { original: row },
      column: columnWithMeta,
      getValue: () => value,
    }),
  };
}

const TableRowNew: React.FC<ComponentProps> = ({
  row,
  columns,
  onChange,
  onSave,
  onCancel,
  canSelect,
  validateRow,
  validationResults = {},
}) => {
  const handleSave = () => {
    const isValid = validateRow(row);
    if (isValid && onSave) {
      onSave();
    }
  };

  const hasAnyError = Object.values(validationResults).some((val) => val?.error);

  const rowClass = classNames('align-middle on-hover', {
    'table-warning': !!validationResults,
    'table-danger': hasAnyError,
  });

  return (
    <tr className={rowClass}>
      {canSelect && <td></td>}
      {columns.map((column) => {
        const columnId = column.id;
        if (!columnId || columnId === 'id') return null;

        const value = row[columnId];
        const validation = validationResults[columnId];

        // simulated cell object to mimic tanstack cellContext
        const psuedoCell = createPseudoCell(column, row, value);

        return (
          <td
            key={columnId}
            title={validation?.message}
            className={classNames('base-cell', column.meta?.style?.cell)}
            style={{
              width: column.meta?.style?.width,
              minWidth: column.meta?.style?.minWidth,
            }}
          >
            <CellContent
              cell={psuedoCell}
              isEditing
              value={value}
              onChange={(newValue) => {
                const updatedRow = { ...row, [columnId]: newValue };
                onChange(updatedRow, column, newValue);
              }}
              row={row}
              isValid={!validation?.error}
            />
          </td>
        );
      })}
      <td className="action-cell">
        <TableEditActions isEditing rowId={row.id} onSave={handleSave} onCancel={onCancel} />
      </td>
    </tr>
  );
};

export default TableRowNew;
